import axios from "axios";
import { createElement } from "react";
import { createRoot } from "react-dom/client";
import { ThemeProvider } from "@mui/material/styles";
import { API_URL } from "../config";
import ConflitoConcorrenciaModal from "../components/ConflitoConcorrenciaModal";
import { createAppTheme } from "../theme/createAppTheme";
import {
  getSystemPrefersDark,
  readStoredThemePreference,
  resolveMode,
} from "../theme/themePreference";
import authService from "./auth";

const apiUrl = API_URL;

let isRefreshing = false;
let failedRequestsQueue = []; // [{ resolve, reject }]
let _isShowingConcurrencyConflict = false;

function _showConcurrencyConflictModal({ entity, message }) {
  if (_isShowingConcurrencyConflict) return;
  _isShowingConcurrencyConflict = true;

  const host = document.createElement("div");
  document.body.appendChild(host);
  const root = createRoot(host);

  const handleClose = () => {
    _isShowingConcurrencyConflict = false;
    root.unmount();
    host.remove();
  };

  const handleReload = () => {
    handleClose();
    window.location.reload();
  };

  const theme = createAppTheme(
    resolveMode(readStoredThemePreference(), getSystemPrefersDark()),
  );

  root.render(
    createElement(
      ThemeProvider,
      { theme },
      createElement(ConflitoConcorrenciaModal, {
        open: true,
        entity,
        message,
        onClose: handleClose,
        onReload: handleReload,
      }),
    ),
  );
}

function _showSessionExpiredModal() {
  let segundosRestantes = 5;

  const messageDiv = document.createElement("div");
  messageDiv.style.cssText = `
    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
    display: flex; align-items: center; justify-content: center;
    background-color: rgba(0, 0, 0, 0.7); z-index: 9999; font-family: Arial, sans-serif;
  `;

  const contentDiv = document.createElement("div");
  contentDiv.style.cssText = `
    background-color: white; padding: 30px; border-radius: 8px;
    text-align: center; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); max-width: 400px;
  `;

  const textDiv = document.createElement("p");
  textDiv.style.cssText = `margin: 0; font-size: 16px; color: #333; line-height: 1.5;`;
  textDiv.textContent = `Sua sessão expirou, faça login novamente para continuar. Redirecionando para a tela de login...`;

  const countdownDiv = document.createElement("p");
  countdownDiv.style.cssText = `margin-top: 20px; font-size: 24px; font-weight: bold; color: #d32f2f;`;
  countdownDiv.textContent = segundosRestantes;

  contentDiv.appendChild(textDiv);
  contentDiv.appendChild(countdownDiv);
  messageDiv.appendChild(contentDiv);
  document.body.appendChild(messageDiv);

  const intervalo = setInterval(() => {
    segundosRestantes--;
    countdownDiv.textContent = segundosRestantes;
    textDiv.textContent = `Sua sessão expirou, faça login novamente para continuar. Redirecionando para a tela de login em ${segundosRestantes} segundo${
      segundosRestantes !== 1 ? "s" : ""
    }...`;

    if (segundosRestantes <= 0) {
      clearInterval(intervalo);
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
  }, 1000);
}

const API_TIMEOUT_MS = 120_000;

const api = axios.create({
  baseURL: apiUrl,
  timeout: API_TIMEOUT_MS,
  headers: {
    "Content-Type": "application/json",
    ...authService.getAuthHeader(),
  },
});

api.interceptors.request.use(async (config) => {
  const user = JSON.parse(localStorage.getItem("user"));
  if (user && user.token) {
    let tokenToUse = user.token;

    try {
      const payload = JSON.parse(atob(user.token.split(".")[1]));
      const isExpired = payload.exp && payload.exp * 1000 < Date.now();

      if (isExpired) {
        if (!isRefreshing) {
          isRefreshing = true;
          try {
            tokenToUse = await authService.refreshAccessToken();
            // Resolver todas as requisições que estavam na fila
            failedRequestsQueue.forEach(({ resolve }) => resolve(tokenToUse));
          } catch (refreshError) {
            // Refresh falhou — rejeitar fila e forçar logout
            failedRequestsQueue.forEach(({ reject }) => reject(refreshError));
            localStorage.removeItem("user");
            delete config.headers.Authorization;
            window.location.href = "/login";
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
            failedRequestsQueue = [];
          }
        } else {
          // Outro refresh já está em andamento — enfileirar esta requisição
          tokenToUse = await new Promise((resolve, reject) => {
            failedRequestsQueue.push({ resolve, reject });
          });
        }
      }
    } catch (e) {
      // Falha ao parsear o token — deixar o servidor decidir
    }

    config.headers.Authorization = `Bearer ${tokenToUse}`;
  }

  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Conflito de concorrência otimista (RowVersion no backend).
    // O servidor retorna 409 com payload { type: "concurrency_conflict", entity, entityId, message }.
    if (
      error.response &&
      error.response.status === 409 &&
      error.response.data?.type === "concurrency_conflict"
    ) {
      _showConcurrencyConflictModal({
        entity: error.response.data?.entity,
        message: error.response.data?.message,
      });
      return Promise.reject(error);
    }

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry // evita loop infinito
    ) {
      originalRequest._retry = true;

      try {
        const newToken = await authService.refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest); // retry com novo token
      } catch (refreshError) {
        // Refresh falhou — exibir modal e redirecionar
        _showSessionExpiredModal();
      }
    }

    return Promise.reject(error);
  }
);

export default api;
